import { h, Fragment } from "preact";
import { useContext, useState } from "preact/hooks";
import { Text } from "preact-i18n";

import { Configs } from "../../helpers/context";
import { formatIntegerCurrencyString } from "../../helpers/formatters";
import { toNegativeValue } from "../../helpers/order";
import {
  INVOICE_ITEM_IMAGE_CLICK,
  INVOICE_ITEM_IMAGE_DOWNLOAD,
  INVOICE_ITEM_IMAGE_DOWNLOAD_ERROR,
} from "../../helpers/event-definitions";
import ImageLightbox from "./ImageLightbox";

const OrderSummaryLineItem = ({
  lineItem,
  isCheckoutPage = false,
  isLast = false,
  imageUrl = null,
  c2Analytics = null,
}) => {
  const configsContext = useContext(Configs);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const lineItemAmount = (lineItem.unitAmount?.value || 0) * (lineItem.quantity || 1);
  const fees = (lineItem && lineItem.fees ? lineItem.fees : []).filter(
    (fee) =>
      fee.appliedBeforeTax || fee.appliedBeforeTax === undefined || !("appliedBeforeTax" in fee),
  );
  const discounts = (lineItem && lineItem.discounts ? lineItem.discounts : [])
    .filter(
      (discount) =>
        discount.appliedBeforeTax ||
        discount.appliedBeforeTax === undefined ||
        !("appliedBeforeTax" in discount),
    )
    .map(toNegativeValue);
  const taxes = lineItem && lineItem.taxes ? lineItem.taxes : [];
  const adjustments = [].concat(fees, discounts, taxes);

  const showThumbnail = imageUrl && !isCheckoutPage && imageLoaded && !imageError;
  const imageName = imageUrl ? imageUrl.split("/").pop() : "";

  const handleThumbnailClick = () => {
    if (c2Analytics?.trackCustomEvent) {
      c2Analytics.trackCustomEvent(INVOICE_ITEM_IMAGE_CLICK);
    }
    setLightboxOpen(true);
  };

  const handleDownload = async () => {
    if (c2Analytics?.trackCustomEvent) {
      c2Analytics.trackCustomEvent(INVOICE_ITEM_IMAGE_DOWNLOAD);
    }
    try {
      const response = await fetch(imageUrl, { mode: "cors" });
      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = imageName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Image download failed", error);
      if (c2Analytics?.trackCustomEvent) {
        c2Analytics.trackCustomEvent(INVOICE_ITEM_IMAGE_DOWNLOAD_ERROR, {
          error: error.message,
        });
      }
    }
  };

  const renderAdjustmentRow = (adjustment) => {
    const isPercent = adjustment.ratePercentage && adjustment.ratePercentage !== "0";

    return (
      <div className={isCheckoutPage ? "item-adjustments-row-checkout" : "item-adjustments-row"}>
        <span>
          {adjustment.name} {isPercent ? `(${adjustment.ratePercentage}%)` : ""}
        </span>
        <span className="order-quantity" />
        {!isCheckoutPage && <span className="order-unit price" />}
        <span className="item-adjustments-amount">
          {formatIntegerCurrencyString(
            adjustment.amount.value,
            configsContext?.configs?.currency,
            configsContext?.configs?.ecommerceLocaleLanguage,
          )}
        </span>
      </div>
    );
  };

  return (
    <Fragment>
      {imageUrl && !isCheckoutPage && (
        <img
          src={imageUrl}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
          style="display:none"
          aria-hidden="true"
        />
      )}
      <div className={isCheckoutPage ? "order-description-checkout" : "order-description"}>
        <span className={showThumbnail ? "order-title order-title-with-image" : "order-title"}>
          {showThumbnail && (
            <button
              type="button"
              className="order-line-item-thumbnail-button"
              onClick={handleThumbnailClick}
              aria-label={`View image for ${lineItem.name}`}
            >
              <img
                className="order-line-item-thumbnail"
                src={imageUrl}
                alt=""
              />
            </button>
          )}
          {lineItem.name}
        </span>
        <span className="order-quantity order-text-center">{lineItem.quantity}</span>
        {!isCheckoutPage && (
          <span className="order-unit price order-text-right">
            {lineItem.unitAmount?.value
              ? formatIntegerCurrencyString(
                  lineItem.unitAmount.value,
                  configsContext?.configs?.currency,
                  configsContext?.configs?.ecommerceLocaleLanguage,
                )
              : ""}
          </span>
        )}
        <span className="cart-size">
          {formatIntegerCurrencyString(
            lineItemAmount,
            configsContext?.configs?.currency,
            configsContext?.configs?.ecommerceLocaleLanguage,
          )}
        </span>
      </div>
      <div className="item-adjustments">
        {adjustments.map((adjustment) => renderAdjustmentRow(adjustment))}
      </div>
      <div style="clear: both;"></div>
      {lineItem.unitAmount?.value && !isCheckoutPage ? (
        <div className="mobile-price">
          <br />
          <div>
            <span>
              <Text id="ORDER_ITEM_UNIT_PRICE">Unit Price: </Text>
            </span>
            <span>
              {lineItem.unitAmount?.value
                ? formatIntegerCurrencyString(
                    lineItem.unitAmount.value,
                    configsContext?.configs?.currency,
                    configsContext?.configs?.ecommerceLocaleLanguage,
                  )
                : ""}
            </span>
          </div>
        </div>
      ) : null}
      {lineItem.notes ? (
        <Fragment>
          <div className="order-note">
            {lineItem.notes
              .filter((note) => note.authorType === "CUSTOMER" && note.content.trim() !== "")
              .map((note, index) => (
                <div key={index}>
                  {index === 0 ? (
                    <div>
                      <span className="order-note-title">
                        <Text id="ORDER_ITEM_NOTE">Note: </Text>
                      </span>
                      <span>{note.content}</span>
                    </div>
                  ) : (
                    <div>
                      <span>{note.content}</span>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </Fragment>
      ) : null}
      {!isLast && <div className="order-summary-divider-dashed"></div>}
      {lineItem.details?.selectedOptions?.length ? (
        <div className="order-modifiers">
          {lineItem.details?.selectedOptions.map((option, i) => {
            return (
              <span className="modifiers">
                {i ? " | " : ""}
                {option.attribute}: {option?.values?.join(", ")}
              </span>
            );
          })}
        </div>
      ) : null}
      {lineItem.details?.selectedAddons?.length ? (
        <div className="order-modifiers">
          {lineItem.details?.selectedAddons.map((addon, i) => {
            return (
              <span className="modifiers">
                {i ? " | " : ""}
                {addon.attribute}:{" "}
                {addon?.values
                  ?.map(
                    (value) =>
                      `${value.name}${
                        value.costAdjustment?.value
                          ? ` (+${formatIntegerCurrencyString(
                              value.costAdjustment.value,
                              configsContext?.configs?.currency,
                              configsContext?.configs?.ecommerceLocaleLanguage,
                            )})`
                          : ""
                      }`,
                  )
                  .join(", ")}
              </span>
            );
          })}
        </div>
      ) : null}
      <ImageLightbox
        imageUrl={imageUrl}
        imageName={imageName}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onDownload={handleDownload}
      />
    </Fragment>
  );
};

export default OrderSummaryLineItem;
